<template>
  <div class="app-tiles-container gs-products-page">
    <div class="gs-products-page__content">
      <v-wait for="products">
        <div v-if="loadError" class="gs-products-page__state">
          <h1>We couldn't load these products</h1>
          <p>{{ loadError }}</p>
          <gd-button variant="primary" @click="retryProducts">Try again</gd-button>
        </div>

        <div class="gs-products-page__hero" v-else-if="funnel">
          <div>
            <p class="gs-products-page__eyebrow">Products</p>
            <h1>{{ funnel.name }}</h1>
            <p>Manage frontend products, upsells, order bumps, checkout links, testing, tracking, and cloning for this funnel.</p>
          </div>
          <div class="gs-products-page__stats">
            <span><strong>{{ products.length }}</strong> products</span>
            <span><strong>{{ bumps.length }}</strong> bumps</span>
          </div>
        </div>
        <div v-if="!loadError" class="gs-products-page__panel">
          <div class="gs-products-page__toolbar">
            <div class="gs-products-page__sort">
              <gd-dropdown
                v-model="selectedOrder"
                size="full"
                :options="sortOrderOptions"
                @change="changedSortOrder"
              />
            </div>
          </div>
          <div class="gs-products-page__tabs">
            <gd-tabs :tabs="getProductTabs" :current-tab="productsTab" :wrapper-class="'px-0 gs-default-tabs'"
              :tab-class="'pb-2 mr-4 gs-default-tabs-item'"
              :tab-active-class="'border-primary border-b-2 text-primary gs-default-tabs-item-active'"
              :line-class="'gs-default-tabs-active-line'" @onClick="handleProductsTabChange" />
            <product-tabs :products="products" :bumps="bumps" :isAdmin="isAdmin" :funnel="funnel"
              :productsTab="productsTab"  :deleteProduct="openDeleteProductConfirmModal"
              :openProjectionsModal="openProjectionsModal" :openTrackingModal="openTrackingModal"
              :deleteBump="openDeleteBumpConfirmModal" :openCloneProduct="openCloneProduct" />
          </div>
        </div>

        <projections-modal v-if="!loadError && pricepointsForProjections.length > 0"
          :pricepointsForProjections="pricepointsForProjections" :funnel="funnel" />
        <tracking-modal v-if="!loadError && selectedProduct" :selectedProduct="selectedProduct" :proofWidgets="proofWidgets" />
        <clone-product-modal v-if="!loadError && selectedProductForCloning" :selectedProduct="selectedProductForCloning"
          :funnel_id="selectedFunnelId" :funnels="funnels" @products="updateProducts" />
        <new-product-modal v-if="!loadError" :isAdmin="isAdmin" :funnel="funnel" />


      </v-wait>
    </div>

    <portal to="headerBreadCrumb">
      <div v-if="funnel?.name" class="flex items-center items-center -ml-4">
        <div class="cursor-pointer text-grey-500" @click="sendToDashboard" v-tooltip="$t('frontend_back_to_dashboard')">
          <arrow-bracket-icon class="h-5 w-5 fill-current"/>
        </div>
        <div class="text-sm font-semibold ml-3">{{ funnel?.name }}</div>
        <chevron-right-icon class="h-5 w-5 fill-current"/>
        <div class="text-sm font-semibold">{{ $t('frontend_dashboard') }}</div>
        <chevron-right-icon class="h-5 w-5 fill-current"/>
      </div>
    </portal>

    <portal to="headerContent">
      <gd-button v-if="!loadError && funnel" variant="primary" @click="createProduct">
        <plus-icon class="h-5 w-5 fill-current"/>
        {{ $t('frontend_add') }}
      </gd-button>
    </portal>
    <confirm-modal
      name="confirm-delete-bump-modal"
      :message-content="$t('frontend_non_revertable')"
      :button-text="$t('frontend_delete_confirm')"
      button-size="auto"
      @confirm="deleteBump"
    />
    <confirm-modal
      name="confirm-delete-product-modal"
      :message-content="$t('frontend_non_revertable')"
      :button-text="$t('frontend_delete_confirm')"
      button-size="auto"
      @confirm="deleteProduct"
    />
    <gs-toast-provider></gs-toast-provider>
  </div>
</template>


<script>
import axios from 'axios';
import Funnel from '@/models/Funnel';
import Product from '@/models/Product';
import Bump from '@/models/Bump';
import ArrowBracketIcon from '@/assets/images/icons/ArrowBracketIcon.svg';
import ChevronRightIcon from '@/assets/images/icons/ChevronRightIcon.svg';
import PlusIcon from '@/assets/images/icons/PlusIcon.svg';
import ProductTabs from './partials/ProductTabs';
import CloneProductModal from './partials/modals/CloneProductModal';
import NewProductModal from './partials/modals/NewProductModal';
import TrackingModal from './partials/modals/TrackingModal';
import ProjectionsModal from './partials/modals/ProjectionsModal';


export default {
  middleware: 'auth',
  scrollToTop: true,
  metaInfo() {
    return { title: 'Products' };
  },
  components: {
    ArrowBracketIcon,
    ChevronRightIcon,
    PlusIcon,
    ProductTabs,
    CloneProductModal,
    NewProductModal,
    TrackingModal,
    ProjectionsModal
  },
  data() {
    return {
      bumpIp: null,
      productId: null,
      showCloningModal: false,
      selectedProductForCloning: null,
      selectedOrder: 'created_at',
      funnel: null,
      selectedProduct: null,
      products: [],
      bumps: [],
      object: {
        name: 'Options',
      },
      checkoutFlow: 0,
      selectedCheckoutFlow: 0,
      trackingFlow: 0,
      selectedTrackingFlow: 0,
      proofWidgets: [],
      checkoutLinksIndex: 0,
      model: {
        checkout_links: [],
      },
      multiPricePointLink: null,
      selectedCheckoutStepType: 1,
      bumpsTableColumns: ['name', 'created_at', 'actions'],
      
      pricepointsForProjections: [],
      selectedProductId: 0,
      selectedFunnelId: 0,
      funnels: [],
      productTabs: [
        {
          title: this.$t('frontend_product_index_tab_all_products'),
          value: 'all',
          show: true
        },
        {
          title: this.$t('frontend_product_index_tab_frontend'),
          value: 'frontend',
          show: true
        },
        {
          title: this.$t('frontend_product_index_tab_upsell_downsell'),
          value: 'upsell_downsell',
          show: true
        },
        {
          title: this.$t('frontend_product_index_tab_order_bumps'),
          value: 'order_bumps',
          show: true
        },
        {
          title: this.$t('frontend_product_index_tab_atc_order_bumps'),
          value: 'atc_order_bumps',
          show: true
        }
      ],
      productsTab: 'all',
      loadError: '',
    };
  },
  computed: {
    isAdmin() {
      return this.$store.state.user.admin;
    },
    getProductTabs() {
      return this.productTabs.filter(tab => tab.show)
    },
    sortOrderOptions() {
      return [
        { value: 'created_at', text: this.$t('frontend_groovesell_created_oldest_to_newest') },
        { value: '-created_at', text: this.$t('frontend_groovesell_created_newest_to_oldest') },
        { value: '-updated_at', text: this.$t('frontend_groovesell_updated_oldest_to_newest') },
        { value: 'updated_at', text: this.$t('frontend_groovesell_updated_newest_to_oldest') },
        { value: 'name', text: this.$t('frontend_name') + ' (A-Z)' },
        { value: '-name', text: this.$t('frontend_name') + ' (Z-A)' },
      ];
    },
  },
  async created() {
    this.$wait.start('products');
    try {
      this.funnel = await Funnel.$find(this.$route.params.id);
      [this.products, this.bumps] = await Promise.all([
        this.funnel.products().$get(),
        this.funnel.bumps().$get(),
      ]);
    } catch (error) {
      const responseMessage = error?.response?.data?.message || error?.response?.data?.error;
      const isUnauthorized = String(responseMessage || '').toLowerCase().includes('unauthorized');
      this.loadError = isUnauthorized
        ? 'Your account does not have access to this funnel.'
        : responseMessage || 'GrooveSell could not retrieve these products. Please try again.';
    } finally {
      this.$wait.end('products');
    }
  },
  methods: {
    reportOperationError(error, fallback) {
      this.$gsToast.error(
        error?.response?.data?.message || error?.response?.data?.error || fallback
      );
    },
    retryProducts() {
      window.location.reload();
    },
    async updateProducts(modal) {
      this.$loader.start('updateProducts');
      try {
        this.products = await this.funnel.products().$get();
        this.$root.$emit('modal-close', modal);
      } catch (error) {
        this.reportOperationError(error, 'GrooveSell could not refresh your products.');
      } finally {
        this.$loader.end('updateProducts');
      }
    },

    async changedSortOrder() {
      this.$loader.start('changedSortOrder');
      try {
        [this.products, this.bumps] = await Promise.all([
          this.funnel.products().orderBy(this.selectedOrder).$get(),
          this.funnel.bumps().orderBy(this.selectedOrder).$get(),
        ]);
      } catch (error) {
        this.reportOperationError(error, 'GrooveSell could not reorder these products.');
      } finally {
        this.$loader.end('changedSortOrder');
      }
    },

    handleProductsTabChange(newTab) {
      this.productsTab = newTab;
    },

    sendToDashboard() {
      this.$router.push({ name: 'funnelDashboard', params: { id: this.funnel.id } });
    },
    async openProjectionsModal(productId) {
      this.$loader.start('openProjectionsModal');
      try {
        this.selectedProduct = await this.funnel.products().$find(productId);
        this.pricepointsForProjections = await this.selectedProduct
          .pricepoints()
          .$get();

        this.$nextTick(() => {
          this.$root.$emit('modal-open', 'projections-modal');
        });
      } catch (error) {
        this.reportOperationError(error, 'GrooveSell could not load these projections.');
      } finally {
        this.$loader.end('openProjectionsModal');
      }
    },

    async openTrackingModal(productId) {
      this.$loader.start('openTrackingModal');
      try {
        this.selectedProduct = await this.funnel.products().$find(productId);
        this.proofWidgets = await this.selectedProduct.proof_widgets().$get();
        if (this.proofWidgets.length > 0) {
          this.proofWidgetsTab = `proof_widget_${this.proofWidgets[0].id}`;
        } else {
          this.proofWidgetsTab = '';
        }

        this.trackingFlow = 'Tracking Link';
        this.selectedCheckoutFlow = 0;
        this.$root.$emit('modal-open', 'tracking-modal');
      } catch (error) {
        this.reportOperationError(error, 'GrooveSell could not load tracking details.');
      } finally {
        this.$loader.end('openTrackingModal');
      }
    },
    async openCloneProduct(productId) {
      this.$loader.start('openCloneProduct');
      try {
        this.selectedFunnelId = this.funnel.id;
        this.funnels = await Funnel.$get();
        this.selectedProductForCloning = this.products.find((product) => { return product.id == productId });
        this.$nextTick(() => {
          this.$root.$emit('modal-open', 'clone-product-modal');
        });
      } catch (error) {
        this.reportOperationError(error, 'GrooveSell could not prepare this product for cloning.');
      } finally {
        this.$loader.end('openCloneProduct');
      }

    },

    createProduct() {
      this.$root.$emit('modal-open', 'new-product-modal');
    },
    openDeleteBumpConfirmModal(id) {
      this.bumpIp = id;
      this.$root.$emit('modal-open', 'confirm-delete-bump-modal');
    },
    openDeleteProductConfirmModal(id) {
      this.productId = id;
      this.$root.$emit('modal-open', 'confirm-delete-product-modal');
    },
   async deleteBump() {
      try {
        const bump = await Bump.$find(this.bumpIp);
        await bump.delete();
        const deleted_product = this.bumps.map((item) => item.id).indexOf(this.bumpIp);
        if (deleted_product !== -1) this.bumps.splice(deleted_product, 1);
        this.$gsToast.success('Order bump deleted.');
      } catch (error) {
        this.reportOperationError(error, 'GrooveSell could not delete this order bump.');
      }
    },

    async makeMainProduct(productId) {
      const { data } = await axios.post('/main-product', {
        id: productId,
      });
      if (data == 1) {
        this.products = await this.funnel.products().$get();
      }
    },

    async deleteProduct() {
      try {
        const product = await Product.$find(this.productId);
        await product.delete();
        const deleted_product = this.products
          .map((item) => item.id)
          .indexOf(this.productId);
        if (deleted_product !== -1) this.products.splice(deleted_product, 1);
        this.$gsToast.success('Product deleted.');
      } catch (error) {
        this.reportOperationError(error, 'GrooveSell could not delete this product.');
      }
    },
  },
};
</script>



<style lang="scss">
.dropdown-toggle {
  background-image: none !important;
  border: 1px solid #dfe4ee !important;
  border-radius: 30px;
}

.dropdown-toggle:hover {
  background: transparent;
}

.dropdown-button-container {
  display: flex;
  align-items: center;

  .btnx {
    border-radius: 5px 0px 0px 5px;
  }

  .btn-drop {
    border-radius: 0px 5px 5px 0px;
    border-left: 1px solid rgba(255, 255, 255, 0.2);
  }
}

.incompleteProduct {
  color: #fff !important;
}

#checkoutLinksWizard {
  width: 100%;
}

#checkoutLinksWizard i.wizard-icon {
  font-style: inherit;
}

#checkoutLinksWizard .wizard-nav-pills li {
  margin-bottom: 20px;
}

#checkoutLinksWizard .wizard-tab-content {
  padding-top: 10px;
  min-width: 90%;
}

#checkoutLinksWizard .wizard-header,
#checkoutLinksWizard .wizard-card-footer {
  padding-top: 0px !important;
}

#products-list-view,
#upsells-list-view,
#order-bumps-list-view,
#bumps-list-view,
#price-points-list-view {
  .VueTables--client table {
    overflow: auto !important;
  }

  .VueTables__search-field {
    margin-bottom: 10px;
  }

  .VueTables__heading {
    color: #8f92ab;
  }

  .VueTables__sort-icon {
    color: #8f92ab;
    margin-left: 8px;
  }

  td {
    font-weight: 400;
    outline: 2px solid transparent;
    outline-offset: 2px;
  }

  .VueTables__child-row-toggler--closed::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 7l-.707031.707031 6.125 6.125 1.875 1.875 1.875-1.875 6.125-6.125-.707031-.707031-6.125 6.125-1.167969 1.167969-1.167969-1.167969-6.125-6.125' /></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }

  .VueTables__child-row {
    text-align: center;
    border-bottom: 1px solid #ddd;
    border-left: 5px solid var(--brand-primary);
  }

  .VueTables__child-row-toggler--open::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 1044.3622l-.707031-.707 6.125-6.125 1.875001-1.875 1.875 1.875 6.125 6.125-.707032.707-6.125-6.125-1.167968-1.168-1.167969 1.168-6.125 6.125' transform='translate(0-1030.3622)'/></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }

  .VueTables__table {
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
  }

  .VueTables__row {
    border-bottom: 1px solid #ddd;
  }

  .VueTables__table th,
  td {
    text-align: left;
    padding: 16px;
  }

  tr.VueTables__child-row>td {
    padding: 0px !important;
  }

  thead {
    border-bottom: 1px solid #ddd;
  }

  .vue-pagination-ad {
    text-align: center;
  }

  .VueTables__limit-field {
    margin-bottom: 12px;
  }

  .VuePagination__count {
    display: none;
  }

  .VueTables__limit {
    display: none;
  }
}

.back-arrow {
  transform: rotate(180deg);

  path {
    fill: #a4a8c0;
  }
}

.gs-products-page {
  color: var(--gs-foreground, #182230);
}

.gs-products-page__content {
  width: min(1220px, calc(100vw - 48px));
  margin: 0 auto;
  padding: 20px 0 42px;
}

.gs-products-page__state {
  max-width: 560px;
  margin: 52px auto;
  padding: 32px;
  text-align: center;
  background: #ffffff;
  border: 1px solid var(--gs-border, #e2e8f0);
  border-radius: 10px;
  box-shadow: 0 18px 40px rgba(31, 37, 72, .08);
}

.gs-products-page__state h1 {
  margin: 0;
  color: var(--gs-foreground, #182230);
  font-size: 24px !important;
}

.gs-products-page__state p {
  margin: 12px 0 24px;
  color: #7c849c;
  line-height: 1.6;
}

.gs-products-page__hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid var(--gs-border, #e2e8f0);
  border-radius: 8px;
  box-shadow: 0 14px 32px rgba(31, 37, 72, .06);
}

.gs-products-page__eyebrow {
  margin: 0 0 5px;
  color: var(--gs-primary, #f22987);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.gs-products-page__hero h1 {
  margin: 0;
  color: var(--gs-foreground, #182230);
  font-size: 24px !important;
  font-weight: 650 !important;
  line-height: 1.25 !important;
}

.gs-products-page__hero p:not(.gs-products-page__eyebrow) {
  max-width: 760px;
  margin: 6px 0 0;
  color: #5f6884;
  font-size: 13px;
  line-height: 1.45;
}

.gs-products-page__stats {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.gs-products-page__stats span {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  min-height: 34px;
  padding: 6px 10px;
  color: #7c849c;
  font-size: 13px;
  background: #f7f8fc;
  border: 1px solid var(--gs-border, #e2e8f0);
  border-radius: 6px;
}

.gs-products-page__stats strong {
  color: var(--gs-foreground, #182230);
  font-size: 15px;
}

.gs-products-page__panel {
  padding: 16px 18px 20px;
  background: #ffffff;
  border: 1px solid var(--gs-border, #e2e8f0);
  border-radius: 8px;
  box-shadow: 0 14px 32px rgba(31, 37, 72, .06);
}

.gs-products-page__toolbar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
}

.gs-products-page__sort {
  width: min(280px, 100%);
}

.gs-products-page__tabs .gs-default-tabs {
  border-bottom: 1px solid var(--gs-border, #e2e8f0);
}

.gs-products-page__tabs .gs-default-tabs-item {
  color: #5f6884;
  font-size: 14px;
  font-weight: 700;
}

.gs-products-page__tabs .gs-default-tabs-item-active {
  color: var(--gs-primary, #f22987) !important;
  border-color: var(--gs-primary, #f22987) !important;
}

@media (max-width: 760px) {
  .gs-products-page__content {
    width: calc(100vw - 28px);
  }

  .gs-products-page__hero {
    flex-direction: column;
  }
}
</style>
